'use client'

/**
 * Hand-ported from `nivex-design/apps/nivex-web-v3/src/components/NestedSurface.tsx` —
 * a generic, domain-free presentational pattern (like `components/brand`, `components/errors`),
 * not a shadcn primitive, so it isn't touched by `scripts/sync-design.mjs`. Re-copy from that
 * source on drift.
 */

import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * The dashboard's outer card surface: a warm, sunken canvas that raised panels sit on.
 *
 * It carries both strokes the layered look depends on — the outer border plus a 1px lighter inset
 * highlight just inside it — and no shadow, so depth comes from fill and stroke alone.
 *
 * Radius is 20px against 8px of padding, which is what makes `CanvasPanel`'s 12px radius concentric
 * with it (inner = outer − padding). Changing one without the other makes the corners look pinched.
 */
export function CardCanvas({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn('flex h-full flex-col gap-2 rounded-[20px] border p-2', className)}
      style={{
        backgroundColor: 'var(--background-canvas)',
        borderColor: 'var(--border-canvas)',
        boxShadow: 'inset 0 0 0 1px var(--border-canvas-inset)',
      }}
    >
      {children}
    </div>
  )
}

/**
 * One raised panel on a `CardCanvas` — its own fill and 1px stroke are what lift it off the canvas.
 * Pass padding via `className`, since a panel holding a chart wants different insets to one holding
 * a single line of text.
 */
export function CanvasPanel({
  children,
  className,
  ...rest
}: { children: ReactNode; className?: string } & Omit<
  ComponentPropsWithoutRef<'div'>,
  'className' | 'children'
>) {
  return (
    <div className={cn('bg-card rounded-[12px] border', className)} {...rest}>
      {children}
    </div>
  )
}
